import { FaSolidPencil, FaSolidPlus, FaSolidTrash } from 'solid-icons/fa'
import { createSignal, For, onMount, Show } from 'solid-js'
import { parse } from 'valibot'
import type { MainMenuObj } from '../../../../components/ModalMenu/MainMenuObj.type'
import { setModalMenu } from '../../../../rx/shared/ModalMenu/ModalMenuSignal'
import { StorageKind } from '../../../core/Storage/StorageKind/StorageKind.enum'
import { StoredItemMetaLocalStorageSchema } from '../../../core/Storage/StoredItem/StoredItemMetaLocalStorage.type'
import Button from '../../../jsx/Button'
import { createDefaultInputProfile } from '../InputProfile/createDefaultInputProfile'
import type { InputProfile } from '../InputProfile/InputProfile.type'
import { deleteInputProfileFromLocalStorage, getAllInputProfilesFromLocalStorage, getInputProfileMetaFromLocalStorage, MAX_INPUT_PROFILES, setInputProfileMetaToLocalStorage } from '../InputProfile/inputProfileLocalStorage'
import { setInputProfileMeta } from '../InputProfile/inputProfileMetaSignal'
import { setInputProfile } from '../InputProfile/inputProfileSignal'
import InputProfileEditorPage from './InputProfileEditorPage'

const InputProfileListPage: MainMenuObj = {
  dname: 'Controls',
  fn() {
    const [profiles, setProfiles] = createSignal<InputProfile[]>([])
    const [selectedId, setSelectedId] = createSignal<number | null>(null)

    const loadProfiles = () => {
      const allProfiles = getAllInputProfilesFromLocalStorage()
      setProfiles(allProfiles)
      
      const meta = getInputProfileMetaFromLocalStorage()
      if (meta?.selected && meta.selected.kind === StorageKind.LocalStorage) {
        setSelectedId(meta.selected.id)
      } else if (allProfiles.length > 0) {
        setSelectedId(allProfiles[0].id)
      }
    }

    onMount(() => {
      loadProfiles()
    })

    const handleCreateProfile = () => {
      const allProfiles = getAllInputProfilesFromLocalStorage()
      if (allProfiles.length >= MAX_INPUT_PROFILES) {
        return
      }

      // Find next available ID
      const existingIds = allProfiles.map(p => p.id)
      let nextId = 1
      while (existingIds.includes(nextId)) {
        nextId++
      }

      const newProfile = createDefaultInputProfile(nextId)
      setInputProfile(newProfile)
      setModalMenu(InputProfileEditorPage)
    }

    const handleDeleteProfile = (id: number) => {
      const allProfiles = getAllInputProfilesFromLocalStorage()
      if (allProfiles.length <= 1) {
        // Delete last profile - replace with new default
        deleteInputProfileFromLocalStorage(id)
        const newProfile = createDefaultInputProfile(1)
        setInputProfile(newProfile)
        loadProfiles()
      } else {
        if (confirm('Delete this profile?')) {
          deleteInputProfileFromLocalStorage(id)
          loadProfiles()
          
          // If deleted profile was selected, select first remaining
          if (selectedId() === id) {
            const remaining = getAllInputProfilesFromLocalStorage()
            if (remaining.length > 0) {
              const newSelected = remaining[0]
              setSelectedId(newSelected.id)
              const newMeta = {
                selected: parse(StoredItemMetaLocalStorageSchema, {
                  kind: StorageKind.LocalStorage,
                  id: newSelected.id,
                }),
              }
              setInputProfileMetaToLocalStorage(newMeta)
              setInputProfileMeta(newMeta)
              setInputProfile(newSelected)
            }
          }
        }
      }
    }

    const handleSelectProfile = (profile: InputProfile) => {
      console.log('handleSelectProfile', profile)
      setSelectedId(profile.id)
      const newMeta = {
        selected: parse(StoredItemMetaLocalStorageSchema, {
          kind: StorageKind.LocalStorage,
          id: profile.id,
        }),
      }
      setInputProfileMetaToLocalStorage(newMeta)
      setInputProfileMeta(newMeta)
      setInputProfile(profile)
    }

    const handleEditProfile = (profile: InputProfile) => {
      setInputProfile(profile)
      setModalMenu(InputProfileEditorPage)
    }

    return (
      <div>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5>Input Profiles</h5>
          <Button
            class="btn btn-primary"
            onClick={handleCreateProfile}
            disabled={profiles().length >= MAX_INPUT_PROFILES}
          >
            <FaSolidPlus /> New Profile
          </Button>
        </div>
        
        <Show when={profiles().length === 0}>
          <p class="text-muted">No profiles found. Create one to get started.</p>
        </Show>

        <ul class="list-group">
          <For each={profiles()}>
            {(profile) => (
              <li 
                onClick={() => handleSelectProfile(profile)}
                class={`list-group-item d-flex justify-content-between align-items-center ${selectedId() === profile.id ? 'active' : ''} cursor-pointer`}
                style={{
                  ...(selectedId() === profile.id ? { 'background-color': 'unset' } : {}),
                  'cursor': 'pointer'
                }}
              >
                <div>
                  <div>Profile {profile.id}</div>
                  <small class="text-muted">
                    {new Date(profile.updated_at).toLocaleString()}
                  </small>
                </div>
                <div>
                  <Button
                    class="btn btn-sm btn-outline-primary me-2"
                    onClick={(event) => {
                      event.stopPropagation()
                      handleEditProfile(profile)
                    }}
                  >
                    <FaSolidPencil /> Edit
                  </Button>
                  <Button
                    class="btn btn-sm btn-outline-danger"
                    onClick={(event) => {
                      event.stopPropagation()
                      handleDeleteProfile(profile.id)
                    }}
                  >
                    <FaSolidTrash />
                  </Button>
                </div>
              </li>
            )}
          </For>
        </ul>
      </div>
    )
  }
}

export default InputProfileListPage

